import { DateTime } from 'luxon';

const DAYS_IN_WEEK: number = 7;

/**
 * Get the date range (first to last day) of the month that contains the
 * given anchor date. This is the range to fetch rotational menus for, since
 * adjacent-month cells are rendered as empty placeholders and never display menus.
 *
 * @param {string} anchorISO - any ISO date within the target month
 * @return {DateRange}
 */
export function getMonthFetchRange(anchorISO: string): DateRange {
	const anchor: DateTime = DateTime.fromISO(anchorISO);
	return {
		start: anchor.startOf('month').toISODate(),
		end: anchor.endOf('month').toISODate()
	};
}

/**
 * Build the calendar grid for the month containing the anchor date.
 *
 * @param {string} anchorISO - any ISO date within the target month
 * @param {boolean} includeWeekends - true for the 7-day Mon-Sun layout, false for 5-day Mon-Fri
 * @return {CalendarWeek[]}
 */
export function buildMonthGrid(anchorISO: string, includeWeekends: boolean): CalendarWeek[] {
	const anchor: DateTime = DateTime.fromISO(anchorISO);
	const monthStart: DateTime = anchor.startOf('month');
	// Normalize to start-of-day so the day-count diffs in the builders are exact
	// (endOf('month') carries a 23:59:59.999 time that rounds the diff up by a whole day)
	const monthEnd: DateTime = anchor.endOf('month').startOf('day');
	return includeWeekends ? buildSevenDayGrid(monthStart, monthEnd) : buildFiveDayGrid(monthStart, monthEnd);
}

/**
 * Build a Monday-Sunday grid covering full weeks, including the adjacent-month
 * days needed to pad the first and last weeks.
 *
 * @param {DateTime} monthStart
 * @param {DateTime} monthEnd
 * @return {CalendarWeek[]}
 */
function buildSevenDayGrid(monthStart: DateTime, monthEnd: DateTime): CalendarWeek[] {
	// Days to step back from the 1st to reach the preceding Monday (Mon=1 -> 0 offset)
	const leadingOffset: number = monthStart.weekday - 1;
	// Days to step forward from the last day to reach the following Sunday (Sun=7)
	const trailingOffset: number = DAYS_IN_WEEK - monthEnd.weekday;

	const gridStart: DateTime = monthStart.minus({ days: leadingOffset });
	const gridEnd: DateTime = monthEnd.plus({ days: trailingOffset });
	const totalDays: number = Math.round(gridEnd.diff(gridStart, 'days').days) + 1;

	const weeks: CalendarWeek[] = [];
	for (let dayIndex = 0; dayIndex < totalDays; dayIndex++) {
		const day: DateTime = gridStart.plus({ days: dayIndex });
		if (dayIndex % DAYS_IN_WEEK === 0) {
			weeks.push([]);
		}
		weeks[weeks.length - 1].push({
			date: day.toISODate()!,
			inMonth: day.month === monthStart.month
		});
	}
	return weeks;
}

/**
 * Shift a date onto a weekday: weekends move forward to the next Monday
 * (direction 1) or backward to the previous Friday (direction -1).
 *
 * @param {DateTime} date
 * @param {number} direction
 * @return {DateTime}
 */
function shiftToWeekday(date: DateTime, direction: number): DateTime {
	if (date.weekday <= 5) {
		return date;
	}
	return direction > 0
		? date.plus({ days: 8 - date.weekday })
		: date.minus({ days: date.weekday - 5 });
}

/**
 * Build a Monday-Friday grid for the month. Weekdays are placed in their
 * real weekday column, so the first/last weeks are padded with the adjacent
 * month's weekdays (flagged `inMonth: false`) rendered as empty placeholders.
 *
 * @param {DateTime} monthStart
 * @param {DateTime} monthEnd
 * @return {CalendarWeek[]}
 */
function buildFiveDayGrid(monthStart: DateTime, monthEnd: DateTime): CalendarWeek[] {
	// First/last weekdays of the month, so a month that starts or ends on a
	// weekend never produces an all-placeholder week
	const firstWeekday: DateTime = shiftToWeekday(monthStart, 1);
	const lastWeekday: DateTime = shiftToWeekday(monthEnd, -1);

	// Pad out to full Monday-Friday weeks
	const gridStart: DateTime = firstWeekday.minus({ days: firstWeekday.weekday - 1 });
	const gridEnd: DateTime = lastWeekday.plus({ days: 5 - lastWeekday.weekday });
	const totalDays: number = Math.round(gridEnd.diff(gridStart, 'days').days) + 1;

	const weeks: CalendarWeek[] = [];
	for (let dayIndex = 0; dayIndex < totalDays; dayIndex++) {
		const day: DateTime = gridStart.plus({ days: dayIndex });

		// Skip weekends (Sat=6, Sun=7)
		if (day.weekday === 6 || day.weekday === 7) {
			continue;
		}

		// Start a new week row on Mondays (gridStart is always a Monday)
		if (day.weekday === 1) {
			weeks.push([]);
		}
		weeks[weeks.length - 1].push({
			date: day.toISODate()!,
			inMonth: day.month === monthStart.month
		});
	}
	return weeks;
}
